import React from 'react';

import { LEGAL } from '../../captivision.config';
import { CVSections } from '../../document.component';
import { formatDate } from '../../i18n/language.component';

// The Privacy Policy, in English: the text that applies. Every other
// privacy.<language>.jsx here is a translation of it, with the same sections in
// the same order, and says that this English text is the one that applies.

const PERMISSIONS = [
    { name: 'Camera', why: 'The Client app films the room and sends the picture to the iPad or Mac. On the iPad or Mac, the picture is used to find faces and see who is talking.' },
    { name: 'Microphone', why: 'The Client app sends sound to the iPad or Mac so it can write captions and match voices to faces.' },
    { name: 'Local Network', why: 'Lets the phones find the iPad or Mac and send video and sound to it over your Wi-Fi.' },
    { name: 'Focus status', why: 'Lets the Client app check whether Do Not Disturb is on, so a phone call does not cut off a camera. The app only reads whether it is on or off.' },
    { name: 'Files (Host only)', why: 'When you record, you choose a folder. The iPad or Mac saves the recording only there.' }
]

const SECTIONS = [
    {
        id: 'summary',
        title: 'The short version',
        body: <>
            <ul>
                <li>Video, sound, captions, speaker photos and names stay on your iPad or Mac and your local Wi-Fi network.</li>
                <li>Nothing from a session is sent to the internet, to us, or to anyone else.</li>
                <li>We have no accounts, no ads, no analytics and no tracking of any kind.</li>
                <li>Payments are handled by Apple. We never see your payment details.</li>
            </ul>
            <p>
                This policy covers two apps made by {LEGAL.companyLegalName} ("we", "us"): <strong>CaptiVision Client</strong>
                {' '}for iPhone and <strong>CaptiVision Host</strong> for iPad and Mac, together "CaptiVision".
            </p>
        </>
    },
    {
        id: 'what',
        title: 'What the apps use, and why',
        body: <>
            <p> CaptiVision asks iOS for a few permissions. Each one is used only for the purpose below, and only on your devices. </p>
            <div className="cv-rowswrap">
                <table className="cv-rows">
                    <thead>
                        <tr><th> Permission </th><th> Why it is needed </th></tr>
                    </thead>
                    <tbody>
                        {PERMISSIONS.map(permission => (
                            <tr key={permission.name}>
                                <th> {permission.name} </th>
                                <td> {permission.why} </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    },
    {
        id: 'where',
        title: 'Where your information goes',
        body: <>
            <h3> On the phones </h3>
            <p>
                The Client app records nothing and stores nothing. It sends video and sound to the iPad or Mac only
                while it is in a session. When the phone leaves the session, everything from that session is
                discarded and the phone's settings (such as auto-lock) are put back the way they were. The phone's
                owner can mute the microphone or leave at any time, and the iPad or Mac cannot override that.
            </p>
            <p>
                To keep faces well lit, the Client app looks for where faces are in the picture. It uses this only
                to set the brightness. It does not identify anyone and does not keep or send any face data.
            </p>

            <h3> On the iPad or Mac </h3>
            <p>
                The Host app does all the work on the iPad or Mac itself: finding faces, deciding who is speaking,
                and turning speech into captions. None of this uses a cloud service.
            </p>
            <p>
                When someone speaks, the iPad or Mac takes a small photo of them from the camera picture so you can
                name them in the Speakers list. You can remove a person from the list at any time; their photo is
                deleted and what they said stays in the transcript.
            </p>
            <p>
                So that one person keeps one name, the iPad or Mac also remembers, for each person it has seen
                speaking, where they were sitting, how their head and shoulders look, and how their voice sounds. All
                of it is worked out on the device and stays there. It is deleted when the session ends, unless you
                turn on <em>Remember the people in this list across sessions</em> in the Speakers list, which keeps it
                on that device for future sessions until you turn the setting off or remove the person. Nothing is
                ever uploaded, and it is never used to look anyone up anywhere else.
            </p>
            <p>
                Recognising people this way may count as biometric data where you are. In some places — for example
                under the GDPR, Illinois' BIPA and Texas' CUBI — you need the consent of the people being filmed. The
                app shows this reminder in the Speakers list; asking the room is the operator's job.
            </p>

            <h3> Between your devices </h3>
            <p>
                Video and sound travel directly from the phones to the iPad or Mac over your local Wi-Fi network.
                They never pass through the internet or through our servers. At present this connection is not
                encrypted, so anyone able to monitor traffic on the same network could in principle see it. Use a
                private network you trust, not an open public one.
            </p>

            <h3> Recordings and transcripts </h3>
            <p>
                The Host app records only when you tap Record, and saves only to the folder you choose. A recording
                can include the video and sound from each camera, the captions, a written transcript with speaker
                names, and a session log. Every camera shows that recording is on for as long as it runs. You
                control these files: you can move, share or delete them like any other file.
            </p>
        </>
    },
    {
        id: 'internet',
        title: 'When the apps use the internet',
        body: <>
            <p> Sessions never need the internet. The Host app goes online only in these cases, and never sends session content: </p>
            <ul>
                <li><strong>Downloading a speech model.</strong> If you choose a Whisper speech model, the iPad or Mac downloads it once from Hugging Face (huggingface.co). If you use Apple's built-in speech, the system downloads the language files from Apple. After that, captions work offline. These providers may see your device's IP address, as with any download, under their own privacy policies.</li>
                <li><strong>Subscriptions.</strong> Buying, restoring or checking CaptiVision Pro is handled by Apple through the App Store. We receive no personal or payment details. See Apple's privacy policy at apple.com/legal/privacy.</li>
            </ul>
        </>
    },
    {
        id: 'collect',
        title: 'What we collect',
        body: <>
            <p>
                Nothing. We do not run servers that receive data from the apps, and the apps contain no analytics,
                advertising or tracking code. If you choose to share crash reports with developers in your device
                settings, Apple may pass us anonymous technical reports. These never contain video, sound, faces,
                names or captions.
            </p>
            <p> If you email us for support, we use your message and email address only to reply to you. </p>
        </>
    },
    {
        id: 'schools',
        title: 'Schools, workplaces and children',
        body: <p>
            CaptiVision is often used in classrooms and workplaces, where the people filmed may include children.
            Because all information stays on the organisation's own devices, the organisation running the session
            is responsible for telling people they are being filmed and captioned, and for getting any consent that
            the law or its own policies require. We do not knowingly collect information from anyone, including
            children under 13.
        </p>
    },
    {
        id: 'rights',
        title: 'Your choices',
        body: <p>
            Since we hold no information about you, there is nothing for us to access, correct or delete. You can
            delete everything CaptiVision stores by deleting recordings you have saved and removing the apps. You
            can turn any permission off at any time in the Settings app.
        </p>
    },
    {
        id: 'changes',
        title: 'Changes to this policy',
        body: <p>
            If we change how CaptiVision handles information, we will update this page and the date at the top
            before the change takes effect.
        </p>
    },
    {
        id: 'contact',
        title: 'Contact',
        body: <p>
            Questions about privacy: <strong>{LEGAL.companyLegalName}</strong>,
            {' '}<a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>.
        </p>
    }
]

const page = {
    title: 'Privacy Policy',
    eyebrow: 'Legal',
    lede: 'CaptiVision is built so that what happens in the room stays in the room. We do not collect, receive or sell any personal information.',
    meta: `Effective ${formatDate('en', LEGAL.effectiveDate)}`,
    body: <CVSections sections={SECTIONS} />
}

export default page;
